/**
 * Mobile-shop module (docs/BUILD-PHASES.md Phase 12) — easyload/wallet/bank
 * agent accounts, their float, and the cash+float dual-effect ledger.
 */
import { z } from 'zod';
import { idSchema, paisaSchema } from './common.js';
import { MOBILE_ACCOUNT_KINDS, MOBILE_TXN_KINDS, MOBILE_WALLET_CATEGORIES } from '../constants.js';
export const COMMISSION_SETTLEMENTS = ['FLOAT', 'CASH', 'MONTHLY_INVOICE'];
/** Basis points, 0-10000 (0-100%) — a sanity cap, not a business rule. */
const bpsSchema = z.coerce.number().int().min(0).max(10_000);
export const createMobileAccountSchema = z.object({
    shopId: idSchema,
    kind: z.enum(MOBILE_ACCOUNT_KINDS),
    provider: z.string().min(1).max(40),
    label: z.string().min(1).max(80),
    msisdn: z.string().max(20).optional(),
    accountNumber: z.string().max(40).optional(),
    openingBalancePaisa: paisaSchema.min(0).default(0),
    lowBalanceAlertPaisa: paisaSchema.min(0).default(0),
    allowNegative: z.boolean().default(false),
    commissionSettlement: z.enum(COMMISSION_SETTLEMENTS).default('FLOAT'),
    walletCategory: z.enum(MOBILE_WALLET_CATEGORIES).optional(),
    /** Default % (docs/Pospak-vs-Dukaan-Cross-Check-Report.docx §3.3) — 0 means unconfigured, matching Dukaan's 1%/2% factory defaults would be a business decision, not a hardcoded one. */
    defaultSendCommissionBps: bpsSchema.default(0),
    defaultReceiveCommissionBps: bpsSchema.default(0),
});
export const updateMobileAccountSchema = createMobileAccountSchema.partial().omit({ shopId: true, kind: true, provider: true });
export const createMobileTxnSchema = z.object({
    accountId: idSchema,
    kind: z.enum(MOBILE_TXN_KINDS),
    customerPhone: z.string().max(20).optional(),
    amountPaisa: paisaSchema.min(1),
    commissionPaisa: paisaSchema.min(0).default(0),
    /**
     * When true, `commissionPaisa` is IGNORED server-side and recomputed from
     * the account's `defaultSendCommissionBps`/`defaultReceiveCommissionBps`
     * (SEND_MONEY uses send, RECEIVE_MONEY uses receive, everything else has
     * no default and falls back to the client-sent amount) — the "Commission"
     * toggle docs/Pospak-vs-Dukaan-Cross-Check-Report.docx §3.3 asks for.
     * False (default) preserves today's behaviour: a manually-typed amount is
     * trusted as-is, same as before this field existed.
     */
    useDefaultCommission: z.boolean().default(false),
    chargesPaisa: paisaSchema.min(0).default(0),
    providerRef: z.string().max(60).optional(),
    note: z.string().max(300).optional(),
    /**
     * ADJUSTMENT: required, the caller states the signed cash/float effect
     * directly (may be negative). Any other kind: cashPaisa is an OPTIONAL
     * override for the MAGNITUDE of cash that actually changed hands at the
     * counter (real cash sometimes differs slightly from the amount plus
     * commission math — a customer rounding up, a shopkeeper adjusting on
     * the spot) — send a positive number; the server still applies the
     * kind's own direction. Omit to keep the computed value. floatPaisa is
     * ignored outside ADJUSTMENT; the wallet balance always follows the
     * mechanical math.
     */
    cashPaisa: paisaSchema.optional(),
    floatPaisa: paisaSchema.optional(),
});
export const listMobileTxnsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    accountId: idSchema.optional(),
    shopId: idSchema.optional(),
});
export const closeMobileDaySchema = z.object({
    declaredClosingFloatPaisa: paisaSchema,
    note: z.string().max(300).optional(),
});
/*
 * Monthly close + operator-statement reconciliation (docs/MOBILE-SHOP.md §6,
 * docs/BUILD-PHASES.md Phase 12's "still to do" gap). A month close rolls up
 * the month's already-closed `MobileDayClose` rows for one account — it takes
 * no declared amount of its own, since "declared" already happened at each
 * day close; the month just aggregates and re-checks it.
 */
export const closeMobileMonthSchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
    month: z.coerce.number().int().min(1).max(12),
    note: z.string().max(300).optional(),
});
export const listMobileMonthClosesQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    accountId: idSchema.optional(),
    shopId: idSchema.optional(),
});
/**
 * One line of the operator's own monthly statement — pasted/typed in or
 * imported from a CSV export of the operator's portal (there is no operator
 * API this codebase talks to, docs/MOBILE-SHOP.md §9). Matched against
 * `MobileTxn` first on `providerRef`, then on `(date, amountPaisa,
 * counterpartyRef)`.
 */
export const mobileStatementRowSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD'),
    direction: z.enum(['SEND', 'RECEIVE']),
    amountPaisa: paisaSchema.min(1),
    counterpartyRef: z.string().max(60).optional(),
    providerRef: z.string().max(60).optional(),
    note: z.string().max(300).optional(),
});
export const importMobileStatementSchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
    month: z.coerce.number().int().min(1).max(12),
    rows: z.array(mobileStatementRowSchema).min(1).max(5000),
});
/**
 * The "one-click create adjustment" docs/MOBILE-SHOP.md §6 asks for on an
 * unmatched reconciliation line: `LOCAL` means "we recorded it, the
 * statement doesn't have it" (the float should come back down/up to match
 * the operator's reality), `STATEMENT` means the operator's statement shows
 * something Pospak never recorded (float should move to reflect it now).
 */
export const createMobileReconciliationAdjustmentSchema = z.object({
    source: z.enum(['LOCAL', 'STATEMENT']),
    index: z.coerce.number().int().min(0),
});
//# sourceMappingURL=mobile.js.map